/**
 * Reconstrucción en párrafos del texto extraído de un PDF (DESIGN.md §11.7; porta `reflow` de la maqueta
 * `docs/auditoria-ui-2026-11-01/maquetas/texto-2723.js`). El texto guardado trae un salto por cada línea del PDF y las
 * llamadas a nota en una línea sola («1», «2»). Todas las reglas son de forma, ninguna de contenido, y ninguna línea se
 * pierde (prueba en `parrafos.test.ts` contra textos reales):
 *  - cabecera: solo si entre las 4 primeras líneas hay «(ORD. N° …)» → una línea; la línea que repite el título se omite
 *    por contenido (nunca por posición);
 *  - una línea que es solo un número entre dos líneas de texto es una llamada a nota → pegada a la palabra, solo si el
 *    documento tiene bloque de notas al final; una línea que termina en abreviatura («art.», «inc.») no cierra párrafo;
 *  - un párrafo termina cuando la línea acaba en « . : ; » y la siguiente empieza con mayúscula, número o signo de
 *    apertura, cuando la siguiente es un ítem numerado («2) …») o cuando hay una línea en blanco; si no, la línea se une
 *    a la siguiente con un espacio (la línea cortada por el ancho del PDF);
 *  - una línea corta en mayúsculas o con numeral romano («I. ANTECEDENTES») es un subtítulo;
 *  - desde «Saluda a usted,» («Saluda a Ud.», «Saluda atentamente a Ud.») hasta la primera nota: firma; las notas al pie al final.
 * La numeración «¶ n» (párrafos e ítems del cuerpo) es la misma que `backend/src/services/documents/parrafos.js`: las
 * reglas se conservan una a una (prueba compartida en `parrafos.test.ts`).
 * Devuelve datos, no HTML: el componente dibuja nodos de React y el texto nunca se interpreta como HTML.
 */

export interface Llamada { pos: number; n: string }
export interface Bloque {
  tipo: 'p' | 'li' | 'h4';
  texto: string;
  /** Llamadas a nota, por posición dentro de `texto` (el superíndice va justo antes del carácter en `pos`). */
  llamadas: Llamada[];
}
export interface Nota { n: string; texto: string }
export interface Documento {
  cabecera: string | null;
  bloques: Bloque[];
  firma: string[];
  notas: Nota[];
}

/** Firma: «Saluda a usted», «Saluda a Ud.», «Saluda atentamente a Ud.» (igual que `backend/.../parrafos.js`). */
export const SALUDO = /^Saluda\s+(?:atentamente\s+|atte\.\s+)?a\s+(?:usted|ud\b)/i;
const ORD = /\(ORD\.?\s*N[°º]/i;
const esNum = (l: string) => /^\d{1,2}$/.test(l);
const ABREV = /\b(?:art|arts|inc|n°|nos|d\.l|d\.f\.l|dfl|ord|res|circ|pág|págs|ref)\.$/i;
const normalizar = (s: string) => s.toLowerCase().replace(/[.\s]+$/, '').replace(/\s+/g, ' ').trim();
/** Subtítulo: numeral romano + mayúsculas, o línea corta en mayúsculas de 2 o más palabras («IUSC» sola no lo es). */
const esSubtitulo = (l: string) =>
  /^(?:[IVX]+\.?\s+)[A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑ\s]{3,60}$/.test(l)
  || (/^[A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑ\s]{7,60}$/.test(l) && l.trim().split(/\s+/).length >= 2 && l.length < 48);
const esItem = (l: string) => /^\d{1,2}\)/.test(l);

export function reflow(raw: string, titulo = ''): Documento {
  const L = (raw || '').split('\n').map(s => s.replace(/\s+$/, ''));
  // Cabecera: solo si entre las 4 primeras líneas hay «(ORD. N° …)» (mayúsculas o no); termina en su «)», aunque caiga
  // en la línea siguiente, y lo que sigue en esa línea es cuerpo (79 oficios de la base no traen saltos de línea).
  const k = L.slice(0, 4).findIndex(l => ORD.test(l));
  let cabecera: string | null = null;
  let extra: string[] = [];
  let i = k + 1;
  if (k >= 0) {
    let kk = k;
    let acum = L[k];
    while (!/\(ORD[^)]*\)/i.test(acum) && kk + 1 < L.length && kk < k + 2) { kk += 1; acum += ` ${L[kk]}`; }
    const m = /\(ORD[^)]*\)\.?/i.exec(acum);
    const corte = m ? m.index + m[0].length : acum.length;
    cabecera = L.slice(0, k).concat(acum.slice(0, corte)).join(' ');
    extra = acum.slice(corte).split(/\s+_{5,}\s+|\s+_{5,}$|^\s*_{5,}\s+/).map(x => x.trim()).filter(Boolean);
    i = kk + 1;
  }
  // La línea que repite el título del documento (ya está en la cabecera de la ventana) se omite por contenido.
  if (!extra.length && titulo && i < L.length && normalizar(L[i]) === normalizar(titulo)) i += 1;
  // Firma y bloque de notas: las notas solo existen si, después de la firma, hay «número solo» seguido de otra línea.
  const s = L.findIndex(l => SALUDO.test(l));
  let n0 = -1;
  if (s >= 0) n0 = L.findIndex((l, j) => j > s && esNum(l) && j + 1 < L.length && !esNum(L[j + 1]));
  const hayNotas = n0 >= 0;
  const finCuerpo = s >= 0 ? s : L.length;
  const cuerpo = extra.concat(L.slice(i, finCuerpo));
  const firma = s >= 0 ? L.slice(s, hayNotas ? n0 : L.length).filter(l => l.trim()) : [];
  const lineasNotas = hayNotas ? L.slice(n0) : [];

  const bloques: Bloque[] = [];
  let texto = '';
  let llamadas: Llamada[] = [];
  let tipo: Bloque['tipo'] = 'p';
  const cierra = () => {
    if (texto.trim()) bloques.push({ tipo, texto: texto.trim(), llamadas });
    texto = '';
    llamadas = [];
    tipo = 'p';
  };
  for (let j = 0; j < cuerpo.length; j += 1) {
    const l = cuerpo[j];
    const next = cuerpo[j + 1] || '';
    // Una línea en blanco o de solo guiones bajos cierra el párrafo (la de guiones no se muestra).
    if (!l.trim() || /^_{5,}$/.test(l)) { cierra(); continue; }
    // Llamada a nota: número solo entre dos líneas de texto, y solo si el documento tiene bloque de notas.
    if (hayNotas && esNum(l) && texto && next && !esNum(next)) {
      texto = texto.replace(/\s+$/, '');
      llamadas.push({ pos: texto.length, n: l });
      continue;
    }
    if (esSubtitulo(l)) { cierra(); bloques.push({ tipo: 'h4', texto: l, llamadas: [] }); continue; }
    if (!texto && esItem(l)) tipo = 'li';
    texto += (texto && !/^[,;.)]/.test(l) ? ' ' : '') + l;
    // Fin de párrafo: termina en . : ; (no en abreviatura) y la siguiente empieza con mayúscula, número o signo de
    // apertura; o la siguiente es un ítem «N)».
    const fin = (/[.:;]$/.test(l) && !ABREV.test(l) && (/^[A-ZÁÉÍÓÚÑ0-9¿¡«"]/.test(next) || !next)) || esItem(next);
    if (fin) cierra();
  }
  cierra();

  const notas: Nota[] = [];
  let actual: Nota | null = null;
  for (const l of lineasNotas) {
    if (esNum(l)) { actual = { n: l, texto: '' }; notas.push(actual); }
    else if (actual && l.trim()) actual.texto += (actual.texto ? ' ' : '') + l;
  }
  return { cabecera, bloques, firma, notas };
}

/** Texto de un bloque con sus llamadas a nota como números (para la prueba de «ninguna línea perdida» y la búsqueda). */
export function textoConLlamadas(b: Bloque): string {
  if (!b.llamadas.length) return b.texto;
  let out = '';
  let desde = 0;
  for (const c of b.llamadas) { out += `${b.texto.slice(desde, c.pos)} ${c.n} `; desde = c.pos; }
  return out + b.texto.slice(desde);
}

/** Todo el documento reconstruido como texto plano, en el orden en que se dibuja. */
export function textoPlano(d: Documento): string {
  return [d.cabecera || '', ...d.bloques.map(textoConLlamadas), ...d.firma, ...d.notas.map(n => `${n.n} ${n.texto}`)].filter(Boolean).join('\n');
}

/** Numera «¶ n» los párrafos y los ítems (los subtítulos no llevan número). */
export function numerarParrafos(bloques: Bloque[]): (Bloque & { n: number | null })[] {
  let n = 0;
  return bloques.map(b => ({ ...b, n: b.tipo === 'h4' ? null : (n += 1) }));
}
